<template>
  <SectionTemplate title="常见问题（FAQs）">
    <XFaq :faq-list="faqList" @contact="handleContact" />
  </SectionTemplate>
</template>

<script setup lang="ts">
import XFaq from '@/design/widgets/faq/faq.vue';
import { useFaqHelper } from '@/design/widgets/faq/useFaqHelper';
import SectionTemplate from '@/design/widgets/landing-section/cn-template.vue';
import { showDynamicContact } from '../../utils/contact';

const linksConfig = {
  'ai-motion': 'https://www.youyan3d.com/blog/Text-to-3D-Camera-Movement-Auto-Generate.html',
  '3d-scenes': 'https://www.youyan3d.com/blog/Massive-3D-Scenes-in-Various-Styles.html',
  'scene-library': 'https://www.youyan3d.com/workspace/scene',
};

// 将链接配置转换为 FaqLink 格式
const links = Object.entries(linksConfig).map(([key, url]) => ({ key, url }));

// 使用 FAQ 辅助工具
const { getLink } = useFaqHelper(links);

// FAQ 数据列表
const faqList = [
  {
    question: '魔珐有言的“场景”是什么，和普通视频背景有什么区别？',
    answer: `我们的场景是专为数字人视频设计的动态、多维空间背景。它不仅是一张图片，更是一个包含光影、空间感和可交互元素的虚拟环境。相比普通静态背景，它能通过3D景深、${getLink('ai-motion', 'AI智能运镜')}创造电影级的沉浸感与视觉冲击力，让视频质感大幅提升。`,
    isOpen: true,
  },
  {
    question: '你们提供的场景主要有哪些类型？',
    answer: `我们提供三大类场景：${getLink('3d-scenes', '沉浸式3D场景')}（用于打造高端虚拟空间）、轻量的2D背景（适用于快速制作与低成本需求）以及透明背景（方便您将数字人合成到任意自制素材中），全面覆盖从营销宣传到专业叙事的各类视频需求。`,
    isOpen: false,
  },
  {
    question: '什么是“AI智能运镜”？它有什么用？',
    answer: `${getLink('ai-motion', 'AI智能运镜')}是我们的核心技术之一。它能自动为您的视频设计并生成多角度、富有动感的镜头运动，如推拉、摇移、环绕等，彻底告别固定机位的单调。这能极大提升视频的节奏感、专业度和观众停留时间，您无需学习复杂的剪辑技巧即可获得电影般的镜头语言。`,
    isOpen: false,
  },
  {
    question: '使用这些场景需要专业的3D设计或视频剪辑技能吗？',
    answer:
      '完全不需要。魔珐有言的设计理念就是“零门槛”。您只需在平台上像选择PPT模板一样，点击即可预览和应用场景。AI会自动处理灯光、角度和运镜，让没有任何专业背景的用户也能在几分钟内产出高质量视频。',
    isOpen: false,
  },
  {
    question: '如何为一个视频选择合适的场景？以及有言的场景适合哪些行业或用途？',
    answer: `平台内的${getLink('scene-library', '场景库')}已按行业（如电商、教育、金融）、风格（如科技、简约、温馨）和用途进行了精细分类。您可以根据视频内容主题快速筛选。电商直播可打造虚拟卖场；企业培训可构建虚拟课堂；产品发布可举办线上虚拟发布会；财经解读可采用数据可视化空间……几乎任何需要通过视频进行沟通、营销和传播的场景，都能找到匹配的解决方案。`,
    isOpen: false,
  },
  {
    question: '我可以对预设场景进行修改吗？比如更换Logo、文字或颜色？',
    answer:
      '完全可以。这是我们产品的核心优势之一。大部分场景支持在线深度编辑，您可以轻松替换品牌Logo、标语文字、主色调以及显示或隐藏特定道具，让通用模板快速变成您的品牌专属场景，实现高效个性化。',
    isOpen: false,
  },
  {
    question: '如果我只需要数字人，不需要背景，该怎么办？',
    answer:
      '这正是我们提供“透明背景”场景的原因。您可以选择透明背景导出视频，即可获得一个去底的、纯净的数字人动态视频。您可以将其无缝嵌入到您已有的PPT、实拍视频或其他剪辑软件中进行二次创作，灵活性极高。',
    isOpen: false,
  },
  {
    question: '与市面上其他提供虚拟背景的工具相比，魔珐有言的核心优势是什么？',
    answer: `我们的核心优势在于 “${getLink('3d-scenes', '高品质3D场景')}+${getLink('ai-motion', 'AI智能运镜')}”的一体化解决方案。许多工具仅提供2D图片或简单的3D模型，而我们将电影级的视觉语言（如镜头运动、景深叙事）自动化，让用户以极低的成本和门槛，获得过去只有专业团队才能制作的动态视觉内容。同时，我们拥有专业的场景设计团队，定期更新场景库，不仅会持续增加新的通用模板，更会紧跟节日热点、市场趋势（如元宇宙、虚拟发布会）推出主题场景，确保您的创作灵感永不枯竭。`,
    isOpen: false,
  },
  {
    question: '如果现有场景库都无法满足我的特定需求，是否有解决方案？',
    answer:
      '有的。我们提供专业的 “场景定制”服务。如果您有独特的品牌空间（如线下展厅、产品车间）需要还原，或有天马行空的创意概念需要实现，我们的专家团队可以为您从零开始，量身打造独一无二的专属3D场景。<a href="javascript:void(0);" data-action="contact-custom">立即联系我们</a>，获取你的专属定制方案。',
    isOpen: false,
  },
];

// 处理联系方式点击
const handleContact = (action: string) => {
  if (action === 'contact-custom') {
    showDynamicContact(1078922, '专属场景定制', '获取你的专属定制方案');
  }
};
</script>
